import { useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Badge,
} from "@tremor/react";
import { toFriendlyTime } from "../../../services/utils";
import DeleteConfirm from "../../../components/DeleteConfirm";
import { PlusCircleIcon, BellAlertIcon } from "@heroicons/react/24/outline";

function ActionsButtons({ onDelete, onEdit }) {
  const [deleteOpen, setDeleteOpen] = useState(false);

  const onDeleteCancel = () => {
    setDeleteOpen(false);
  };

  const onDeleteConfirm = () => {
    onDelete();
    setDeleteOpen(false);
  };

  return (
    <div className="flex space-x-2 justify-end">
      <button
        onClick={onEdit}
        className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-gray-500"
        title="Edit rule"
      >
        <EditActiveIcon className="h-4 w-4" />
        <span className="hidden sm:ml-1 sm:inline">Edit</span>
      </button>
      <button
        onClick={() => setDeleteOpen(true)}
        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-950/40 rounded-md hover:bg-red-200 dark:hover:bg-red-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-red-500"
        title="Delete rule"
      >
        <DeleteActiveIcon className="h-4 w-4" />
        <span className="hidden sm:ml-1 sm:inline">Delete</span>
      </button>
      <DeleteConfirm
        isOpen={deleteOpen}
        onConfirm={onDeleteConfirm}
        closeModal={onDeleteCancel}
        message="Are you sure you want to delete this rule?"
      />
    </div>
  );
}

// email or http icon for type
function NoData({ open }) {
  return (
    <>
      <div className="mt-4 flex h-52 items-center justify-center rounded-tremor-small border border-dashed border-tremor-border p-4 dark:border-dark-tremor-border">
        <div className="text-center">
          <BellAlertIcon
            className="mx-auto h-7 w-7 text-tremor-content-subtle dark:text-dark-tremor-content-subtle"
            aria-hidden={true}
          />

          <p className="mt-2 text-sm sm:text-tremor-default font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
            No Rules
          </p>
          <p className="text-xs sm:text-tremor-default text-tremor-content dark:text-dark-tremor-content">
            Get started by creating a new alert and receive notifications.
          </p>
          <p></p>
          <Button
            icon={PlusCircleIcon}
            onClick={open}
            type="button"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-tremor-small bg-tremor-brand px-3 py-2 text-tremor-default font-medium text-tremor-brand-inverted shadow-tremor-input hover:bg-tremor-brand-emphasis"
          >
            Add Rule
          </Button>
        </div>
      </div>
    </>
  );
}

export default function RulesTable({ rules, onDelete, onEdit, onCreate, onToggleRule }) {
  const condtitions = {
    gt: "Greater than",
    lt: "Less than",
    eq: "Equal to",
    ne: "Not equal to",
    gte: "Greater than or equal to",
    lte: "Less than or equal to",
  };

  return rules && rules.length === 0 ? (
    <NoData open={() => onCreate()} />
  ) : (
    <div className="overflow-x-auto">
      <Table className="mt-6 min-w-full">
        <TableHead className="border-gray-500 bg-gray-100 dark:bg-gray-800">
          <TableRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell className="hidden sm:table-cell">Threshold</TableHeaderCell>
            <TableHeaderCell className="hidden md:table-cell">Action Type</TableHeaderCell>
            <TableHeaderCell className="hidden lg:table-cell">Last Triggered</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell className="px-4 py-3 text-right">
              Settings
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rules.map((rule) => (
            <TableRow key={`${rule._id}`}>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{rule.name}</span>
                  <span className="text-xs text-gray-600 dark:text-gray-400 sm:hidden mt-1">
                    Ch {rule.condition.channel} {condtitions[rule.condition.operator]} {rule.condition.value}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 md:hidden mt-1">
                    {rule.action.type}
                  </span>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <div className="flex flex-col">
                  <span>{`Channel ${rule.condition.channel}`}</span>
                  <span className="text-xs">
                    {condtitions[rule.condition.operator]} <Badge size="xs">{rule.condition.value}</Badge>
                  </span>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell">{rule.action.type}</TableCell>
              <TableCell className="hidden lg:table-cell">{toFriendlyTime(rule.triggeredAt)}</TableCell>
              <TableCell>
                <button
                  onClick={() => onToggleRule(rule)}
                  role="switch"
                  aria-checked={rule.enabled}
                  aria-label={rule.enabled ? 'Disable rule' : 'Enable rule'}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    rule.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-700'
                  }`}
                  title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${ /* dark-ok: knob stays white in dark so it stays visible on the track */
                      rule.enabled ? 'translate-x-[1.125rem]' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </TableCell>
              <TableCell className="text-right">
                <ActionsButtons
                  onDelete={() => onDelete(rule._id)}
                  onEdit={() => onEdit(rule)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EditActiveIcon(props) {
  return (
    <svg
      {...props}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4 13V16H7L16 7L13 4L4 13Z"
        fill="#e3e3e3"
        stroke="#666666"
        strokeWidth="2"
      />
    </svg>
  );
}

function DeleteActiveIcon(props) {
  return (
    <svg
      {...props}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="5"
        y="6"
        width="10"
        height="10"
        fill="#e3e3e3"
        stroke="#666666"
        strokeWidth="2"
      />

      <path d="M3 6H17" stroke="#666666" strokeWidth="2" />
      <path d="M8 6V4H12V6" stroke="#666666" strokeWidth="2" />
    </svg>
  );
}
